import { Button, Drawer, Input, Select, Table, Tag, Timeline, Tooltip } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import SectionWrapper from "../../components/SectionWrapper";
import ShowTotal from "../../components/ShowTotal";
import { AppStateContext } from "../../context/AppContext";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/langHelper";
import { Severty, ShowToast } from "../../helper/toast";
import useDebounce from "../../hooks/useDebounce";
import useRequest from "../../hooks/useRequest";

const orderStatuses = ["pending", "confirmed", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];
const paymentStatuses = ["pending", "paid", "failed", "refunded", "cod_pending"];
const shipmentStatuses = ["pending", "packed", "shipped", "in_transit", "out_for_delivery", "delivered"];

const formatCurrency = (value, currency = "INR") => `${currency === "INR" ? "Rs." : currency} ${Number(value || 0).toLocaleString("en-IN")}`;
const getUserLabel = (user) => user?.name || user?.user_name || user?.email || "-";
const statusLabel = (value) => String(value || "-").replaceAll("_", " ");

function OrderManagement() {
  const heading = "Order Management";
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const requestRef = useRef(request);

  const [searchText, setSearchText] = useState("");
  const [orderStatus, setOrderStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [shipmentForm, setShipmentForm] = useState({ courierName: "", trackingId: "", trackingUrl: "" });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const debouncedSearchText = useDebounce(searchText, 300);

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  useEffect(() => {
    setPageHeading(heading);
  }, [setPageHeading]);

  useEffect(() => {
    setShipmentForm({
      courierName: selected?.courierName || "",
      trackingId: selected?.trackingId || "",
      trackingUrl: selected?.trackingUrl || "",
    });
  }, [selected]);

  const fetchData = useCallback(
    (pageConfig = { current: 1, pageSize: 10 }) => {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(pageConfig?.current || 1),
        pageSize: String(pageConfig?.pageSize || 10),
      });

      if (debouncedSearchText) params.set("search", debouncedSearchText);
      if (orderStatus) params.set("orderStatus", orderStatus);
      if (paymentStatus) params.set("paymentStatus", paymentStatus);

      requestRef.current({
        url: `${apiPath.orders}?${params.toString()}`,
        method: "GET",
        onSuccess: ({ data }) => {
          setLoading(false);
          setList((data?.docs || []).map((item) => ({ ...item, key: item._id })));
          setPagination((prev) => ({
            ...prev,
            current: pageConfig?.current || 1,
            pageSize: pageConfig?.pageSize || 10,
            total: data?.totalDocs || 0,
          }));
        },
        onError: (error) => {
          setLoading(false);
          ShowToast(error?.response?.data?.message || error?.message || "Failed to load orders", Severty.ERROR);
        },
      });
    },
    [debouncedSearchText, orderStatus, paymentStatus],
  );

  useEffect(() => {
    fetchData({ current: 1, pageSize: pagination.pageSize });
  }, [debouncedSearchText, orderStatus, paymentStatus, fetchData, pagination.pageSize]);

  const updateSelected = (patch, successMessage = "Order updated successfully") => {
    if (!selected) return;
    setUpdating(true);
    requestRef.current({
      url: `${apiPath.orders}/${selected._id}/status`,
      method: "PATCH",
      data: patch,
      onSuccess: ({ data }) => {
        setUpdating(false);
        setSelected(data?.order || selected);
        ShowToast(successMessage, Severty.SUCCESS);
        fetchData(pagination);
      },
      onError: (error) => {
        setUpdating(false);
        ShowToast(error?.response?.data?.message || error?.message || "Failed to update order", Severty.ERROR);
      },
    });
  };

  const handleShipmentUpdate = () => {
    updateSelected(
      {
        shipmentStatus: selected?.shipmentStatus || "pending",
        courierName: shipmentForm.courierName,
        trackingId: shipmentForm.trackingId,
        trackingUrl: shipmentForm.trackingUrl,
      },
      "Shipment details updated successfully",
    );
  };

  const columns = [
    {
      title: lang("Order"),
      dataIndex: "_id",
      key: "_id",
      render: (_, record) => <span className="cap">#{String(record?._id || "").slice(-8).toUpperCase()}</span>,
    },
    {
      title: lang("Customer"),
      dataIndex: "userId",
      key: "userId",
      render: (_, { userId }) => <span className="cap" title={userId?.email || ""}>{getUserLabel(userId)}</span>,
    },
    {
      title: lang("Products"),
      dataIndex: "items",
      key: "items",
      render: (_, { items }) => <span>{Number(items?.length || 0)}</span>,
    },
    {
      title: lang("Total"),
      dataIndex: "finalAmount",
      key: "finalAmount",
      render: (_, record) => <span>{formatCurrency(record?.finalAmount, record?.currency)}</span>,
    },
    {
      title: lang("Order Status"),
      dataIndex: "orderStatus",
      key: "orderStatus",
      render: (_, { orderStatus }) => <Tag color={orderStatus === "cancelled" ? "red" : orderStatus === "delivered" ? "green" : "blue"}>{statusLabel(orderStatus)}</Tag>,
    },
    {
      title: lang("Payment"),
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (_, { paymentStatus }) => <Tag color={paymentStatus === "paid" ? "green" : paymentStatus === "failed" ? "red" : "orange"}>{statusLabel(paymentStatus)}</Tag>,
    },
    {
      title: lang("Created At"),
      dataIndex: "created_at",
      key: "created_at",
      render: (_, { created_at }) => <span>{created_at ? new Date(created_at).toLocaleString() : "-"}</span>,
    },
    {
      title: lang("Action"),
      key: "action",
      render: (_, record) => (
        <Tooltip title={lang("View")} color={"purple"}>
          <Button className="primary_btn btnStyle" onClick={() => setSelected(record)}>{lang("View")}</Button>
        </Tooltip>
      ),
    },
  ];

  const itemColumns = [
    { title: lang("Product"), dataIndex: "name", key: "name", render: (_, record) => <span className="cap">{record?.name || record?.productId?.title || "-"}</span> },
    { title: lang("SKU"), dataIndex: "sku", key: "sku", render: (_, { sku }) => <span>{sku || "-"}</span> },
    { title: lang("Qty"), dataIndex: "quantity", key: "quantity", render: (_, { quantity }) => <span>{Number(quantity || 0)}</span> },
    { title: lang("Price"), dataIndex: "price", key: "price", render: (_, record) => <span>{formatCurrency(record?.price, selected?.currency)}</span> },
    { title: lang("Subtotal"), dataIndex: "subtotal", key: "subtotal", render: (_, record) => <span>{formatCurrency(record?.subtotal, selected?.currency)}</span> },
  ];

  return (
    <>
      <SectionWrapper
        cardHeading={heading}
        extra={
          <div className="w-100 text-head_right_cont flex-wrap gap-2">
            <div className="pageHeadingSearch">
              <Input.Search className="searchInput" placeholder={lang("Search orders, customer or payment id")} onChange={(e) => setSearchText(e.target.value)} allowClear />
            </div>
            <Select style={{ minWidth: 170 }} placeholder="Order status" value={orderStatus} onChange={setOrderStatus} options={[{ value: "", label: "All Orders" }, ...orderStatuses.map((value) => ({ value, label: statusLabel(value) }))]} />
            <Select style={{ minWidth: 170 }} placeholder="Payment status" value={paymentStatus} onChange={setPaymentStatus} options={[{ value: "", label: "All Payments" }, ...paymentStatuses.map((value) => ({ value, label: statusLabel(value) }))]} />
          </div>
        }
      >
        <h4 className="text-right mb-1cont-space cont-space">{ShowTotal(pagination.total || 0)}</h4>
        <div className="table-responsive customPagination checkBoxSrNo">
          <Table
            loading={loading}
            columns={columns}
            dataSource={list}
            pagination={{ current: pagination.current, defaultPageSize: 10, responsive: true, total: pagination.total, showSizeChanger: true, showQuickJumper: true, pageSize: pagination.pageSize, pageSizeOptions: ["10", "20", "30", "50"] }}
            onChange={(nextPagination) => fetchData(nextPagination)}
            className="ant-border-space"
          />
        </div>
      </SectionWrapper>

      <Drawer title="Order Details" width={860} open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected ? (
          <>
            <div className="mb-3">
              <p><strong>Customer:</strong> {getUserLabel(selected?.userId)} {selected?.userId?.email ? `(${selected.userId.email})` : ""}</p>
              <p><strong>Phone:</strong> {selected?.shippingAddress?.phone || selected?.userId?.mobile || "-"}</p>
              <p><strong>Total:</strong> {formatCurrency(selected?.finalAmount, selected?.currency)}</p>
              <p><strong>Payment Method:</strong> {statusLabel(selected?.paymentMethod)}</p>
              <p><strong>Razorpay Order:</strong> {selected?.razorpayOrderId || "-"}</p>
              <p><strong>Razorpay Payment:</strong> {selected?.razorpayPaymentId || "-"}</p>
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
              <Select disabled={updating} style={{ minWidth: 220 }} value={selected.orderStatus} onChange={(value) => updateSelected({ orderStatus: value })} options={orderStatuses.map((value) => ({ value, label: statusLabel(value) }))} />
              <Select disabled={updating} style={{ minWidth: 220 }} value={selected.paymentStatus} onChange={(value) => updateSelected({ paymentStatus: value })} options={paymentStatuses.map((value) => ({ value, label: statusLabel(value) }))} />
            </div>

            <div className="mb-3 p-3" style={{ border: "1px solid #f0f0f0", borderRadius: 12, background: "#fafafa" }}>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="mb-0">Shipment Details</h5>
                <Button className="primary_btn btnStyle" loading={updating} onClick={handleShipmentUpdate}>
                  Update Shipment Details
                </Button>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <Select
                  disabled={updating}
                  style={{ minWidth: 220 }}
                  value={selected.shipmentStatus || "pending"}
                  onChange={(value) => setSelected((prev) => ({ ...prev, shipmentStatus: value }))}
                  options={shipmentStatuses.map((value) => ({ value, label: statusLabel(value) }))}
                />
                <Input
                  style={{ maxWidth: 220 }}
                  placeholder="Courier Name"
                  value={shipmentForm.courierName}
                  onChange={(e) => setShipmentForm((prev) => ({ ...prev, courierName: e.target.value }))}
                />
                <Input
                  style={{ maxWidth: 220 }}
                  placeholder="Tracking ID"
                  value={shipmentForm.trackingId}
                  onChange={(e) => setShipmentForm((prev) => ({ ...prev, trackingId: e.target.value }))}
                />
                <Input
                  style={{ maxWidth: 260 }}
                  placeholder="Tracking URL"
                  value={shipmentForm.trackingUrl}
                  onChange={(e) => setShipmentForm((prev) => ({ ...prev, trackingUrl: e.target.value }))}
                />
              </div>
            </div>

            <h5>Shipping Address</h5>
            <p>
              {selected?.shippingAddress?.fullName}, {selected?.shippingAddress?.addressLine1}
              {selected?.shippingAddress?.addressLine2 ? `, ${selected.shippingAddress.addressLine2}` : ""}, {selected?.shippingAddress?.city}, {selected?.shippingAddress?.state} - {selected?.shippingAddress?.pincode}, {selected?.shippingAddress?.country}
            </p>

            <h5>Products</h5>
            <Table columns={itemColumns} dataSource={(selected?.items || []).map((item, index) => ({ ...item, key: `${item?.productId?._id || item?.productId}-${index}` }))} pagination={false} />

            <h5 className="mt-3">Order Timeline</h5>
            <Timeline
              items={[
                { color: "blue", children: `Created: ${selected?.created_at ? new Date(selected.created_at).toLocaleString() : "-"}` },
                { color: selected?.isPaid ? "green" : "gray", children: `Paid: ${selected?.paidAt ? new Date(selected.paidAt).toLocaleString() : statusLabel(selected?.paymentStatus)}` },
                { color: selected?.deliveredAt ? "green" : "gray", children: `Delivered: ${selected?.deliveredAt ? new Date(selected.deliveredAt).toLocaleString() : "Pending"}` },
              ]}
            />
          </>
        ) : null}
      </Drawer>
    </>
  );
}

export default OrderManagement;
